import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Banknote, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };
const STATUS = {
  owed: { c: C.amber, t: 'OWED — TRANSFER DUE' },
  returned: { c: C.green, t: 'TRANSFERRED' },
  unevidenced_collection: { c: C.blue, t: 'UNEVIDENCED' },
  unregistered: { c: C.slate, t: 'NOT REGISTERED' },
  manual_rail: { c: C.amber, t: 'OWED' },
  pending: { c: C.slate, t: 'PENDING' },
};
const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Chip({ text, color, testid }) {
  return <span data-testid={testid} className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide"
    style={{ background: color + '15', color }}>{text}</span>;
}

export function ReturnPremiumCard() {
  const api = useApi();
  const { user } = useAuth();
  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get('/payments/return-premium', { params: { limit: 25 } })
      .then(r => setData(r.data))
      .catch(() => setData(null));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post('/payments/return-premium/run', { limit: 1000 });
      const d = r.data || {};
      toast.success(`${d.owed || 0} owed (${money(d.amount_owed)}) · ${d.unevidenced_collection || 0} unevidenced (${money(d.amount_unevidenced)})`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'The register could not run'); }
    setRunning(false);
  };

  const record = async () => {
    setSaving(true);
    try {
      await api.post(`/payments/return-premium/${form.cancellation_id}/record`, {
        amount: Number(form.amount), method: form.method, reference: form.reference,
        transferred_on: form.transferred_on,
      });
      toast.success(`${money(form.amount)} recorded against ${form.policy_number}`);
      setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'The transfer could not be recorded'); }
    setSaving(false);
  };

  if (!data) return null;
  const rail = data.rail || {};
  const p = data.promise || {};
  const t = data.totals || {};

  return (
    <Card className="rounded-md bg-white p-4" data-testid="return-premium-card"
      style={{ border: '1px solid rgba(136,150,168,0.12)', borderLeft: `3px solid ${(t.owed_count || 0) > 0 ? C.amber : C.blue}` }}>
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <Banknote className="w-4 h-4" style={{ color: C.blue }} />
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.slate }}>PF-10 · Return Premium</h3>
        <Chip testid="rp-rail-chip" text="BANK TRANSFER — RECORDED BY HAND" color={C.blue} />
        <Chip testid="rp-wired-chip" text={p.wired ? 'WIRED' : 'NOT WIRED · MEASURED'} color={p.wired ? C.green : C.amber} />
        <div className="ml-auto flex items-center gap-1.5">
          <button onClick={load} className="p-1 rounded hover:bg-slate-50" data-testid="rp-refresh" style={{ color: C.slate }}><RefreshCw className="w-3.5 h-3.5" /></button>
          {isAdmin && (
            <Button size="sm" onClick={run} disabled={running} className="text-xs rounded-sm h-7" style={{ background: C.navy }} data-testid="rp-run-btn">
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Re-run the register'}
            </Button>
          )}
        </div>
      </div>
      <p className="text-[11px] mb-2.5" style={{ color: C.slate }} data-testid="rp-rail-note">
        Premium moves broker to agency on account terms, so it goes back the same way: within {data.window_business_days || 5} business
        days of cancellation, by transfer, recorded here with its bank reference. The platform never holds the money — it holds the
        obligation, the deadline and the evidence.
      </p>

      <div className="flex gap-5 flex-wrap mb-2.5">
        <div><p className="text-2xl font-bold" style={{ color: (t.owed_total || 0) > 0 ? C.amber : C.slate }} data-testid="rp-owed-total">{money(t.owed_total)}</p>
          <p className="text-[10px] uppercase" style={{ color: C.slate }}>owed — transfer due ({t.owed_count || 0})</p></div>
        <div><p className="text-2xl font-bold" style={{ color: C.green }} data-testid="rp-returned-total">{money(t.returned_total)}</p>
          <p className="text-[10px] uppercase" style={{ color: C.slate }}>transferred ({t.returned_count || 0})</p></div>
        <div><p className="text-2xl font-bold" style={{ color: C.blue }} data-testid="rp-unevidenced-total">{money(t.unevidenced_total)}</p>
          <p className="text-[10px] uppercase" style={{ color: C.slate }}>unevidenced ({t.unevidenced_count || 0})</p></div>
        <div><p className="text-2xl font-bold" style={{ color: C.navy }} data-testid="rp-met-rate">{p.met_rate_pct != null ? `${p.met_rate_pct}%` : '—'}</p>
          <p className="text-[10px] uppercase" style={{ color: C.slate }}>within the promise ({p.measured || 0} measured)</p></div>
        <div><p className="text-2xl font-bold" style={{ color: C.slate }} data-testid="rp-no-premium">{t.no_premium_count || 0}</p>
          <p className="text-[10px] uppercase" style={{ color: C.slate }}>nothing was ever received</p></div>
      </div>

      <p className="text-[11px] mb-2.5 p-2 rounded-sm" data-testid="rp-unevidenced-note"
        style={{ background: 'rgba(26,76,124,0.06)', color: C.navy }}>
        <b>{money(t.unevidenced_total)}</b> across {t.unevidenced_count || 0} cancellation(s) would be owed back, but those invoices were
        marked paid without a receipt behind them — the platform cannot show the premium was ever collected, so it is not claimed as a debt.
        Confirm each against the broker account before transferring. {t.no_premium_count || 0} more never received premium at all.
        {(t.unregistered_count || 0) > 0 && <span> {t.unregistered_count} cancellation(s) are not registered yet — re-run the register.</span>}
      </p>

      {(data.rows || []).length > 0 ? (
        <table className="w-full text-xs" data-testid="rp-table">
          <thead><tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
            <th className="py-1 pr-2">Policy</th><th className="py-1 pr-2">Owed to</th><th className="py-1 pr-2">Cancelled</th>
            <th className="py-1 pr-2 text-right">Owed</th><th className="py-1 pr-2 text-right">Transferred</th>
            <th className="py-1 pr-2">Due</th><th className="py-1">State</th>
          </tr></thead>
          <tbody>
            {data.rows.map((c, i) => (
              <tr key={`${c.cancellation_id}-${i}`} className="border-t align-top" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`rp-row-${c.policy_number}`}>
                <td className="py-1.5 pr-2" style={{ color: C.navy }}>
                  <span className="font-mono text-[10px] mr-1" style={{ color: C.slate }}>{c.policy_number}</span>{c.business_name}
                </td>
                <td className="py-1.5 pr-2 text-[11px]" style={{ color: C.slate }}>{c.broker_name || '—'}</td>
                <td className="py-1.5 pr-2 text-[11px]" style={{ color: C.slate }}>{String(c.effective_date || '').slice(0, 10)}</td>
                <td className="py-1.5 pr-2 text-right" style={{ color: C.navy }}>{money(c.owed)}</td>
                <td className="py-1.5 pr-2 text-right font-medium" style={{ color: (c.returned || 0) > 0 ? C.green : C.slate }}>{money(c.returned)}</td>
                <td className="py-1.5 pr-2 text-[11px]" style={{ color: C.slate }}>{String(c.deadline || '').slice(0, 10) || '—'}</td>
                <td className="py-1.5">
                  <Chip testid={`rp-status-${c.policy_number}`} text={(STATUS[c.status] || STATUS.pending).t} color={(STATUS[c.status] || STATUS.pending).c} />
                  {c.basis === 'pre_rail_legacy' && <Chip text="PRE-RAIL" color={C.slate} />}
                  {c.reference && <span className="block text-[10px] font-mono mt-0.5" style={{ color: C.slate }}>{c.reference}</span>}
                  {isAdmin && c.status !== 'returned' && (
                    <button className="block text-[10px] mt-1 underline" style={{ color: C.blue }}
                      data-testid={`rp-record-${c.policy_number}`}
                      onClick={() => setForm({ cancellation_id: c.cancellation_id, policy_number: c.policy_number, amount: c.owed || '', method: 'EFT', reference: '', transferred_on: new Date().toISOString().slice(0, 10) })}>
                      record transfer
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-[11px]" style={{ color: C.slate }} data-testid="rp-empty">
          Nothing is owed back — no cancellation carries an unreturned premium.
        </p>
      )}

      {form && (
        <div className="mt-3 p-3 rounded-sm" data-testid="rp-record-form" style={{ background: 'rgba(29,158,117,0.06)' }}>
          <p className="text-[11px] mb-2" style={{ color: C.navy }}>
            Record the bank transfer that returned premium on <b>{form.policy_number}</b>. The reference is what makes it reconcilable.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Input value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              placeholder="Amount" className="rounded-sm text-xs h-8 w-28" data-testid="rp-amount-input" />
            <Input value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))}
              placeholder="Method (EFT)" className="rounded-sm text-xs h-8 w-28" data-testid="rp-method-input" />
            <Input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))}
              placeholder="Bank reference" className="rounded-sm text-xs h-8 w-52" data-testid="rp-reference-input" />
            <Input type="date" value={form.transferred_on} onChange={e => setForm(f => ({ ...f, transferred_on: e.target.value }))}
              className="rounded-sm text-xs h-8 w-36" data-testid="rp-date-input" />
            <Button size="sm" disabled={saving || !form.reference.trim() || !(Number(form.amount) > 0)}
              className="text-xs rounded-sm h-8" style={{ background: C.green }} onClick={record} data-testid="rp-record-submit">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Record transfer'}
            </Button>
            <button className="text-[11px]" style={{ color: C.slate }} onClick={() => setForm(null)} data-testid="rp-record-cancel">cancel</button>
          </div>
        </div>
      )}
    </Card>
  );
}
